import { useState } from "react";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const ContactSection = () => {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const { t, lang } = useLanguage();

  const fontFamily = lang === "ar" ? "'Noto Sans Arabic', 'Nunito', sans-serif" : "'Nunito', sans-serif";

  const contactInfo = [
    {
      icon: Mail,
      label: lang === "ar" ? "البريد الإلكتروني" : "Email",
      value: "hello@drdee.ps",
      href: "mailto:hello@drdee.ps",
    },
    {
      icon: Phone,
      label: lang === "ar" ? "الهاتف" : "Phone",
      value: "+972 59 244 8088",
      href: "tel:+970592448088",
    },
    {
      icon: MapPin,
      label: lang === "ar" ? "الموقع" : "Location",
      value: lang === "ar" ? "فلسطين" : "Palestine",
      href: "#",
    },
  ];

  const [sending, setSending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const { error } = await supabase.functions.invoke("send-contact-email", {
        body: formData,
      });
      if (error) throw error;
      setSubmitted(true);
      setFormData({ name: "", email: "", message: "" });
      toast.success(lang === "ar" ? "تم إرسال رسالتك بنجاح!" : "Message sent successfully!");
      setTimeout(() => setSubmitted(false), 3000);
    } catch (err) {
      console.error("Contact form error:", err);
      toast.error(lang === "ar" ? "حدث خطأ، حاول مرة أخرى" : "Something went wrong, please try again");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative overflow-hidden px-6 py-24 sm:px-10 lg:px-16"
      style={{ background: "linear-gradient(180deg, #fff 0%, #fdf2f5 100%)" }}
    >
      <div
        className="pointer-events-none absolute top-20 right-10 h-72 w-72 rounded-full opacity-10 blur-3xl"
        style={{ background: "radial-gradient(circle, #E88CA4, transparent 70%)" }}
      />

      <div className="relative mx-auto max-w-5xl">
        <p className="mb-3 text-xs font-semibold uppercase tracking-[0.25em]" style={{ fontFamily, color: "#E88CA4" }}>
          {t("contact.label")}
        </p>

        <h2
          className="text-4xl font-bold sm:text-5xl overflow-visible"
          style={{
            fontFamily: "'Poppins', sans-serif",
            background: "linear-gradient(135deg, #E88CA4 0%, #a8405c 50%, #f8c8d8 100%)",
            backgroundSize: "200% 100%",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            color: "transparent",
            WebkitTextFillColor: "transparent",
            animation: "gradientShift 5s ease infinite",
            lineHeight: 1.4,
            paddingTop: "0.15em",
          }}
        >
          {t("contact.title")}
        </h2>

        <div className="mt-6 h-[2px] w-16 rounded-full" style={{ background: "#E88CA4" }} />

        <div className="mt-10 grid gap-12 md:grid-cols-2">
          <div className="flex flex-col gap-5">
            <p className="text-base leading-relaxed text-gray-600" style={{ fontFamily }}>
              {t("contact.desc")}
            </p>

            <div className="mt-2 flex flex-col gap-4">
              {contactInfo.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  className="group flex items-center gap-4 rounded-2xl border border-gray-100 bg-white p-4 transition-all duration-300 hover:border-[#E88CA4]/30 hover:shadow-lg hover:shadow-[#E88CA4]/10"
                >
                  <div
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-all duration-300 group-hover:scale-110"
                    style={{ background: "rgba(232,140,164,0.12)" }}
                  >
                    <item.icon size={18} style={{ color: "#E88CA4" }} strokeWidth={1.8} />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-400" style={{ fontFamily }}>
                      {item.label}
                    </p>
                    <p className="text-sm font-medium text-gray-700" style={{ fontFamily }}>
                      {item.value}
                    </p>
                  </div>
                </a>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            <div>
              <label
                className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-400"
                style={{ fontFamily }}
              >
                {t("contact.name")}
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700 outline-none transition-all duration-300 focus:border-[#E88CA4]/50 focus:shadow-md focus:shadow-[#E88CA4]/10"
                style={{ fontFamily }}
                placeholder={t("contact.namePlaceholder")}
              />
            </div>

            <div>
              <label
                className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-400"
                style={{ fontFamily }}
              >
                {t("contact.email")}
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700 outline-none transition-all duration-300 focus:border-[#E88CA4]/50 focus:shadow-md focus:shadow-[#E88CA4]/10"
                style={{ fontFamily }}
                placeholder={t("contact.emailPlaceholder")}
              />
            </div>

            <div>
              <label
                className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-400"
                style={{ fontFamily }}
              >
                {t("contact.message")}
              </label>
              <textarea
                required
                rows={4}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full resize-none rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700 outline-none transition-all duration-300 focus:border-[#E88CA4]/50 focus:shadow-md focus:shadow-[#E88CA4]/10"
                style={{ fontFamily }}
                placeholder={t("contact.messagePlaceholder")}
              />
            </div>

            <button
              type="submit"
              disabled={sending}
              className="group mt-1 flex items-center justify-center gap-2 rounded-full px-8 py-3.5 text-sm font-bold text-white transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60 disabled:pointer-events-none"
              style={{
                fontFamily,
                background: "linear-gradient(180deg, rgba(232,140,164,0.95) 0%, rgba(200,100,130,0.9) 100%)",
                boxShadow: "inset 0 1px 3px rgba(255,255,255,0.4), 0 6px 20px rgba(232,140,164,0.35)",
              }}
            >
              {sending ? (
                <Loader2 size={15} className="animate-spin" />
              ) : submitted ? (
                t("contact.sent")
              ) : (
                <>
                  {t("contact.send")}
                  <Send size={15} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
